import { ConfigProvider, Form } from 'antd';
import cls from 'classnames';
import { useTranslation } from 'next-i18next';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import InfoModal from './InfoModal';
import CustomRadioGroup from './RadioButtonGroup';
import RichTextArea from './RichTextArea';
import styles from './meta.module.scss';
import { aiWriterIcon } from '../../assets';
import { useCSR } from '../../hooks/useCSR';
import { useMirrorEnable } from '../../hooks/useMirrorEnable';
import {
  MirrorExperiment,
  setInfoModalVisibility,
  setPersonaLogicModalVisibility,
  SuggestNpcSetting,
  AgentSuggestNpcSetting,
  updateModifiedFields,
  updateSettingInfo,
  updateImgInfo,
  updateVoiceInfo,
  setEditingImageUrl,
  setVoiceUrl,
  useAppDispatch,
  useAppSelector,
} from '../../state';
import { track } from '../../track';
import { EGender, EModerationDecision } from '../../types';
import { Gender, NpcSettingSuggestScene } from '../../types/meta';
import { isTalkie } from '../../utils';
import { PAGE } from '../EditHeader';
import { ENpcStatusTrackText } from '../Home';
import { getNpcStatus } from '../Home/NpcList';
import { InfoTip } from '../Info';
import { ITextArea } from '../Textarea';
import PersonaLogicModal from './PersonaLogicModal';

interface IMetaAreaFormProps {}

const { Item } = Form;

const theme = {
  token: {
    colorBgContainer: '#FFFFFF0A',
    colorBorder: 'transparent',
    colorTextPlaceholder: '#FFFFFF59',
    colorText: '#FFFFFFBF',
    colorTextTertiary: '#FFFFFF59',
    borderRadius: 8,
    lineHeight: 1.5,
    colorSplit: '#F0F0F51A',
  },
  components: {
    Input: {
      paddingBlock: 12,
      paddingInline: 16,
      inputFontSize: 16,
    },
    Form: {
      labelColor: '#FFFFFFE5',
      labelFontSize: 16,
      labelHeight: 22,
      labelRequiredMarkColor: '#FFFFFF59',
      verticalLabelPadding: '0 0 16px',
      itemMarginBottom: 28,
    },
  },
};

export const fieldMappings: any = {
  npc_name: 'isNameModified',
  desc: 'isDescModified',
  prologue: 'isPrologueModified',
  gender: 'isGenderModified',
  long_desc: 'isAdvancedSettingModified',
};

const HoverIconButton = ({
  src,
  label,
  onClick,
  styles,
  disabled = false,
}: {
  src: string;
  label: string;
  onClick: () => void;
  styles: Record<string, string>;
  disabled?: boolean;
}) => {
  return (
    <div
      className={cls([styles.actionGroup, disabled ? styles.disabledActionGroup : ''])}
      onClick={disabled ? undefined : onClick}
    >
      <img src={src} width="14" height="14" alt={label} />
      <span className={styles.autoGenerateButton}>{label}</span>
    </div>
  );
};

const customizeRequiredMark =
  (styles: Record<string, string>) =>
  (label: ReactNode, { required }: { required: boolean }) => (
    <div className={styles.customizeRequiredMark}>
      {label}
      {required && <div className={styles.requiredMark}>*</div>}
    </div>
  );

export function MetaAreaForm() {
  const { t } = useTranslation();

  const genderOptions = [
    { value: Gender.Male, label: t('gender_male') },
    { value: Gender.Female, label: t('gender_female') },
    { value: Gender.NoHuman, label: t('gender_other') },
  ];

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const {
    settingInfo,
    npcId,
    isInfoModalVisible,
    isPersonaLogicModalVisible,
    statusInfo,
    modifiedFields,
  } = useAppSelector((state) => state.edit);

  const npcName = Form.useWatch('npc_name', form);
  const desc = Form.useWatch('desc', form);
  const prologue = Form.useWatch('prologue', form);

  const { edit_version, final_decision, last_submitted_time } = statusInfo;
  const status = getNpcStatus(statusInfo);

  const hasEditVersion = edit_version > -1 || (last_submitted_time || 0) > 0;
  const isVisibleAll = final_decision === EModerationDecision.VisibleAll;
  const Disabled = isVisibleAll && hasEditVersion && settingInfo.gender !== EGender.NotDefined;

  const [formCompleted, setFormCompleted] = useState(false);

  useEffect(() => {
    const isComplete = [npcName, desc, prologue].every(Boolean);
    setFormCompleted(isComplete);
  }, [npcName, desc, prologue]);

  useEffect(() => {
    form.setFieldsValue(settingInfo);
  }, [form, npcId, settingInfo]);

  const requiredMark = useMemo(() => customizeRequiredMark(styles), [styles]);

  const handleInfoModal = async () => {
    track.send('ai_write_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      ai_write_click_position: 'name',
    });
    dispatch(setInfoModalVisibility(true));
    dispatch(
      SuggestNpcSetting({
        params: NpcSettingSuggestScene.NewBasic,
      })
    );
  };

  const handleAgentInfoModal = async () => {
    track.send('agent_info_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      agent_info_click_position: 'agent_creation',
    });

    // 调用 AgentSuggestNpcSetting 异步 thunk
    dispatch(AgentSuggestNpcSetting());
  };

  const handlePersonaLogic = async () => {
    try {
      await form.validateFields(['npc_name', 'desc', 'prologue']);
      track.send('ai_write_click', PAGE, {
        npc_id: npcId,
        ugc_npc_approving_status: ENpcStatusTrackText[status],
        ai_write_click_position: 'persona_and_prompt',
      });
      dispatch(setPersonaLogicModalVisibility(true));
      dispatch(SuggestNpcSetting({ params: NpcSettingSuggestScene.advanced }));
    } catch (e) {}
  };

  const handleValuesChange = async (changedValues: any, allValues: any) => {
    const updatedModifiedFields: any = { ...modifiedFields };
    Object.keys(changedValues).forEach((key) => {
      const modifiedField = fieldMappings[key];
      if (modifiedField) {
        updatedModifiedFields[modifiedField] = true;
      }
    });

    dispatch(
      updateSettingInfo({
        ...settingInfo,
        ...allValues,
      })
    );
    dispatch(updateModifiedFields(updatedModifiedFields));
  };

  const isCSR = useCSR();

  if (!isCSR) return null;

  return (
    <>
      <ConfigProvider theme={theme}>
        <Form
          name="meta"
          form={form}
          layout="vertical"
          validateTrigger="onBlur"
          requiredMark={requiredMark}
          className={styles.formContainer}
          onValuesChange={handleValuesChange}
        >
          {/* 昵称 */}
          <Item
            label={
              <div className={styles.logicLabelContainer}>
                <div className={styles.labelWithAction}>
                  <span>{t('nickname')}</span>
                  <span className={styles.requiredMark}>*</span>
                </div>
                <HoverIconButton
                  label={t('auto_generate')}
                  styles={styles}
                  onClick={handleInfoModal}
                  src={aiWriterIcon.src}
                />
              </div>
            }
            name="npc_name"
            required={false}
            rules={[{ required: true, message: t('nickname_required') }]}
          >
            {/* 18 字符（9 个汉字 */}
            <ITextArea
              minRows={1}
              maxRows={1}
              wrapperCls={styles.nameInput}
              placeholder={t('nickname_placeholder')}
              maxLength={isTalkie ? 18 : 9}
              showCount
            />
          </Item>

          {/* 性别 */}
          <div className={styles.labelContainer}>
            <div className={styles.labelLeft}>
              <span className="mr-[6px]">{t('gender')}</span>
              {isTalkie ? <InfoTip title={t('gender_tip')} place="top" /> : null}
              <span className={styles.requiredMark}>*</span>
            </div>
            <Item
              name="gender"
              trigger="onChange"
              rules={[{ required: true, message: t('gender_required') }]}
              valuePropName="value"
            >
              <CustomRadioGroup disabled={Disabled} options={genderOptions} styles={styles} />
            </Item>
          </div>

          {/* 新人设与回复逻辑 */}

          <Item name="long_desc" label={t('long_desc')} required>
            <div className={styles.newTip}>
              <span className={styles.icon}>{t('update')}</span>
              <span className={styles.tip}>{t('update_long_desc_tip')}</span>
            </div>
            <Item
              name="long_desc"
              rules={[{ required: true, message: t('long_desc_required') }]}
              label={t('long_desc')}
              noStyle
            >
              {/* 附加设定 = 3000字符（1500汉字）； */}
              <RichTextArea
                id="edit_setting_info_long_desc"
                initialValue={settingInfo.long_desc}
                nicknameSelector={(state) => state.edit.settingInfo.npc_name}
                minRows={7}
                maxRows={12}
                wrapperCls={styles.ITextArea}
                placeholder={t('long_desc_placeholder')}
                maxLength={isTalkie ? 4000 : 2000}
                showCount
              />
            </Item>
          </Item>

          {/* 简介 */}
          <Item label={t('desc')} name="desc" required>
            <div className={styles.newTip}>
              <span className={styles.icon}>{t('update')}</span>
              <span className={styles.tip}>{t('update_desc_tip')}</span>
            </div>
            <Item
              label={t('desc')}
              name="desc"
              rules={[{ required: true, message: t('desc_required') }]}
              noStyle
            >
              {/* 简介 = 2000 字符（1000 汉字）； */}
              <ITextArea
                minRows={3}
                maxRows={8}
                wrapperCls={styles.ITextArea}
                placeholder={t('desc_placeholder')}
                maxLength={isTalkie ? 2000 : 1000}
                showCount
              />
            </Item>
          </Item>

          {/* 开场白 */}
          <Item
            label={t('prologue')}
            name="prologue"
            rules={[{ required: true, message: t('prologue_required') }]}
          >
            {/* 500 字符（250 汉字）  */}
            <ITextArea
              minRows={2}
              maxRows={4}
              wrapperCls={styles.ITextArea}
              placeholder={t('prologue_placeholder')}
              maxLength={isTalkie ? 500 : 250}
              showCount
            />
          </Item>
        </Form>
      </ConfigProvider>

      {/* Modals */}
      <InfoModal styles={styles} isOpen={isInfoModalVisible} />
      <PersonaLogicModal isOpen={isPersonaLogicModalVisible} styles={styles} />
    </>
  );
}

export default MetaAreaForm;
